import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Loader2, FileText, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import apiService from '@/services/api';
import { useToast } from '@/hooks/use-toast';

interface Quote {
  id: number;
  quote_number: string;
  service_name: string;
  amount: number;
  status: string;
  created_at: string;
  urgency_level: string;
  property_type: string;
  postcode: string;
}

const statusConfig: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
  pending: {
    label: 'Pending',
    className: 'bg-yellow-100 text-yellow-800',
    icon: <Clock className="w-3 h-3 mr-1" />,
  },
  provider_assigned: {
    label: 'Provider Assigned',
    className: 'bg-blue-100 text-blue-800',
    icon: <AlertCircle className="w-3 h-3 mr-1" />,
  },
  customer_approved: {
    label: 'Approved',
    className: 'bg-green-100 text-green-800',
    icon: <CheckCircle className="w-3 h-3 mr-1" />,
  },
  completed: {
    label: 'Completed',
    className: 'bg-emerald-100 text-emerald-800',
    icon: <CheckCircle className="w-3 h-3 mr-1" />,
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-red-100 text-red-800',
    icon: <XCircle className="w-3 h-3 mr-1" />,
  },
};

const QuotesListPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/customer/quotes');
      if (response?.data && Array.isArray(response.data)) {
        setQuotes(response.data);
      } else {
        setQuotes([]);
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to load quotes', variant: 'destructive' });
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const config = statusConfig[status] ?? {
      label: status?.replace(/_/g, ' ') ?? 'Unknown',
      className: 'bg-gray-100 text-gray-800',
      icon: null,
    };
    return (
      <Badge className={`${config.className} flex items-center`}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  return (
    <>
      <Helmet>
        <title>Quotes | Virewire Property Services</title>
        <meta name="description" content="View and manage your quotes in your Virewire customer dashboard." />
      </Helmet>

      <DashboardLayout>
        <div className="w-full space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#00543b]">Quotes</h1>
              <p className="text-muted-foreground mt-1">Review and manage your service quotes</p>
            </div>
            <Button
              className="bg-[#00543b] hover:bg-[#003d2d]"
              onClick={() => navigate('/dashboard/request-service')}
            >
              <Plus className="h-4 w-4 mr-2" />
              Request New Quote
            </Button>
          </div>

          {loading ? (
            <Card>
              <CardContent className="flex justify-center items-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-[#00543b]" />
              </CardContent>
            </Card>
          ) : quotes.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg font-semibold text-gray-700 mb-2">No quotes yet</p>
                <p className="text-sm text-gray-500 mb-6">Request a quote to get started with a service</p>
                <Button
                  className="bg-[#00543b] hover:bg-[#003d2d]"
                  onClick={() => navigate('/dashboard/request-service')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Request Your First Quote
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                          Quote #
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                          Service
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                          Location
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                          Urgency
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                          Status
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wide">
                          Amount
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                          Date
                        </th>
                        <th className="px-6 py-4" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {quotes.map((quote) => (
                        <tr
                          key={quote.id}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/dashboard/quotes/${quote.id}`)}
                        >
                          <td className="px-6 py-4">
                            <span className="font-mono text-sm text-gray-700">
                              {quote.quote_number || `Q-${quote.id}`}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <p className="font-semibold text-gray-900 text-sm">
                              {quote.service_name || 'Service'}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-600">
                              {[quote.property_type, quote.postcode].filter(Boolean).join(' · ') || '—'}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            {quote.urgency_level ? (
                              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                quote.urgency_level === 'emergency'
                                  ? 'bg-red-100 text-red-800'
                                  : quote.urgency_level === 'urgent'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {quote.urgency_level.charAt(0).toUpperCase() + quote.urgency_level.slice(1)}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {getStatusBadge(quote.status)}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className="font-semibold text-gray-900">
                              {quote.amount
                                ? `£${(Number(quote.amount) / 100).toFixed(2)}`
                                : <span className="text-gray-400 font-normal">TBC</span>
                              }
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-600">
                              {new Date(quote.created_at).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/quotes/${quote.id}`);
                              }}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    {quotes.length} quote{quotes.length !== 1 ? 's' : ''} total
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DashboardLayout>
    </>
  );
};

export default QuotesListPage;
